<app-legals-header title="Privacy Policy" />

<section class="legal-content section" aria-label="Privacy Policy content">
  <div class="legal-content__inner container subheading-dark">
    <p class="legal-content__intro">Publication date: September 11, 2026</p>

    <div class="legal-section__body">
      <p>
        Cuebric, Inc. d/b/a EVRN (“EVRN,” “we”, “us” or “our”) is an AI-powered platform that
        enables users to generate immersive 3D worlds and environments from 2D images. This Privacy
        Policy describes how Cuebric processes personal information that we collect through our
        digital and online properties or services that link to this Privacy Policy (including as
        applicable, our website, web application, member platform, electronic communications, and
        social media pages) as well as our marketing activities and other activities described in
        this Privacy Policy (collectively, the “Service”).
      </p>
      <p>
        This Privacy Policy applies to our subscription/retail customers. This Privacy Policy does
        not apply to information that we process on behalf of our enterprise customers while
        providing services to them. Our use of information that we process on behalf of our
        enterprise customers is governed by our agreements with such customers. If you have concerns
        regarding your personal information that we process on behalf of our enterprise customer,
        please direct your concerns to the enterprise customer with which you have a relationship.
      </p>
    </div>

    <nav class="legal-toc" aria-label="Table of Contents">
      <h2 class="legal-section__title title-small-dark">Table of Contents</h2>
      <ol>
        <li>
          <a routerLink="/privacy-policy" fragment="section-1">Personal information we collect</a>
        </li>
        <li>
          <a routerLink="/privacy-policy" fragment="section-2"
            >How we use your personal information</a
          >
        </li>
        <li>
          <a routerLink="/privacy-policy" fragment="section-3"
            >How we share your personal information</a
          >
        </li>
        <li><a routerLink="/privacy-policy" fragment="section-4">Data retention</a></li>
        <li><a routerLink="/privacy-policy" fragment="section-5">Your choices</a></li>
        <li><a routerLink="/privacy-policy" fragment="section-6">Other sites and services</a></li>
        <li><a routerLink="/privacy-policy" fragment="section-7">Security</a></li>
        <li>
          <a routerLink="/privacy-policy" fragment="section-8">International data transfer</a>
        </li>
        <li><a routerLink="/privacy-policy" fragment="section-9">Children and teens</a></li>
        <li>
          <a routerLink="/privacy-policy" fragment="section-10">Changes to this Privacy Policy</a>
        </li>
        <li><a routerLink="/privacy-policy" fragment="section-11">How to contact us</a></li>
        <li>
          <a routerLink="/privacy-policy" fragment="section-12"
            >U.S. State Privacy Rights Notice at Collection</a
          >
        </li>
        <li><a routerLink="/privacy-policy" fragment="section-13">Notice to European users</a></li>
      </ol>
    </nav>

    <section class="legal-section" aria-labelledby="section-1">
      <h2 id="section-1" class="legal-section__title title-small-dark">
        1. Personal information we collect
      </h2>
      <div class="legal-section__body">
        <p>We collect certain personal information from you in order to operate the Service.</p>
        <div>
          <p>
            <strong>Information you provide to us.</strong> You may directly provide us with
            information about you during your use of the Service. This may include:
          </p>
          <ul>
            <li>
              <strong>Contact data,</strong> such as your first and last name, salutation, email
              address, billing and mailing addresses, professional title, and company name.
            </li>
            <li>
              <strong>Demographic data,</strong> such as your city, state, country of residence, and
              postal code.
            </li>
            <li>
              <strong>Profile data,</strong> such as account name, the username and password that
              you may set to establish an online account on the Service, biographical details, job
              title, avatar, photograph or picture, date of birth, interests, preferences, links to
              your profiles on social media networks, and any other information that you add to your
              account profile.
            </li>
            <li>
              <strong>Payment data</strong> needed to complete transactions, including payment card
              information or bank account number. We use a third-party vendor to directly collect
              and process your payment card information, as described further below.
            </li>
            <li>
              <strong>Communications data</strong> based on our exchanges with you, including when
              you contact us through the Service, communicate with us via email, phone, chat
              features, social media, or otherwise.
            </li>
            <li>
              <strong>Marketing data,</strong> such as your preferences for receiving our marketing
              communications and details about your engagement with them.
            </li>
            <li>
              <strong>User content data,</strong> such as any communications, comments, reviews,
              questions, messages, photos, music, videos, works of authorship, activations, inputs,
              and other content or information that you generate, transmit, or otherwise make
              available on the Service, including associated metadata. Metadata includes information
              about collected content and how that content has been modified, as well as any
              keywords, location information, and other similar data associated with the content.
            </li>
            <li>
              <strong>Biometric data,</strong> such as certain voice and facial data that is used to
              generate 3D replicas of any persons depicted in your 2D reference images. We do not
              use biometric data for purposes of uniquely identifying anyone.
            </li>
            <li>
              <strong>Other data</strong> not specifically listed here, which we will use as
              described in this Privacy Policy or as otherwise disclosed at the time of collection.
            </li>
          </ul>
        </div>
        <p>
          <strong>Data about others.</strong> We may offer features that help users invite their
          contacts to use the Service, and we may collect contact details about these invitees so we
          can deliver their invitations. Please do not refer someone to us or share their contact
          details with us unless you have their permission to do so.
        </p>
        <div>
          <p>
            <strong>Third-party sources.</strong> We may combine personal information we receive
            from you with personal information of the kinds identified above that we obtain from
            other sources, such as:
          </p>
          <ul>
            <li>
              <strong>Data providers,</strong> such as information services, data licensors/brokers,
              lead generators and cooperative databases.
            </li>
            <li>
              <strong>Service providers</strong> that provide services on our behalf or help us
              operate the Service or our business.
            </li>
            <li>
              <strong>Partners,</strong> such as joint marketing partners and event co-sponsors.
            </li>
          </ul>
        </div>
        <div>
          <p>
            <strong>Automatic data collection.</strong> We, our service providers, and our
            advertising partners may automatically log information about you, your computer or
            mobile device, and your interaction over time with the Service, our communications and
            other online services, such as:
          </p>
          <ul>
            <li>
              <strong>Device data,</strong> such as your computer or mobile device’s operating
              system type and version, manufacturer and model, browser type, screen resolution, RAM
              and disk size, CPU usage, device type (e.g., phone, tablet), IP address, unique
              identifiers (including identifiers used for advertising purposes), language settings,
              mobile device carrier, radio/network information (e.g., Wi-Fi, LTE, 3G), and general
              location information such as city, state or geographic area.
            </li>
            <li>
              <strong>Location data,</strong> which may be approximate (for example, as inferred
              from network data or device settings).
            </li>
            <li>
              <strong>Communication interaction data</strong> such as your interactions with our
              email, chat or other communications (e.g., whether you open and/or forward emails) –
              we may do this through use of pixel tags (which are also known as clear GIFs), which
              may be embedded invisibly in our emails enabling us to detect if you have opened or
              forwarded a message.
            </li>
            <li>
              <strong>Online activity data,</strong> such as pages or screens you viewed, search
              history, how long you spent on a page or screen, the website you visited before
              browsing to the Service, navigation paths between pages or screens, information about
              your activity on a page or screen, access times and duration of access, and whether
              you have opened our emails or clicked links within them.
            </li>
          </ul>
        </div>
        <p>
          <strong>Tracking technologies.</strong> Some of the automatic collection described above
          is facilitated by cookie and similar technologies. For more information, see our
          <a routerLink="/cookie-policy">Cookie Policy</a>.
        </p>
      </div>
    </section>

    <section class="legal-section" aria-labelledby="section-2">
      <h2 id="section-2" class="legal-section__title title-small-dark">
        2. How we use your personal information
      </h2>
      <div class="legal-section__body">
        <p>
          We may use your personal information for the following purposes or as otherwise described
          at the time of collection:
        </p>
        <div>
          <p>
            <strong>Service delivery and operations.</strong> We may use your personal information
            to:
          </p>
          <ul>
            <li>provide and operate the Service;</li>
            <li>process your requests, and transactions;</li>
            <li>enable security features of the Service;</li>
            <li>
              personalize the service, including remembering the devices from which you have
              previously logged in and remembering your selections and preferences as you navigate
              the Service;
            </li>
            <li>establish and maintain your user profile on the Service;</li>
            <li>
              communicate with you about the Service, including by sending Service-related
              announcements, updates, security alerts, user-to-user communications, and support and
              administrative messages;
            </li>
            <li>
              provide support for the Service, and respond to your requests, questions and feedback;
            </li>
            <li>to collect and share user testimonials; and</li>
            <li>fraud prevention.</li>
          </ul>
        </div>
        <p>
          <strong>Service improvement, research, and analytics.</strong> We may use information
          about you to analyze user activity on the Service and improve the Service and our
          business. As part of these efforts, we may create aggregated, de-identified or other
          anonymous data from personal information. We may use this anonymous or de-identified data
          and share it with third parties for any lawful business purposes. We do not attempt to
          reidentify deidentified information derived from personal information, except for the
          purpose of testing whether our deidentification processes comply with applicable law.
        </p>
        <p>
          <strong>Marketing.</strong> We may collect and use your personal information for direct
          marketing purposes. We may send you direct marketing communications and may personalize
          these messages based on your needs and interests. You may opt-out of our marketing
          communications as described in the
          <a routerLink="/privacy-policy" fragment="opt-out-of-communications"
            >Opt out of marketing</a
          >
          section.
        </p>
        <div id="compliance-and-protection">
          <p>
            <strong>Compliance and protection.</strong> We may use your personal information to:
          </p>
          <ul>
            <li>
              comply with applicable laws, lawful requests, and legal process, such as to respond to
              subpoenas, court orders, investigations or requests from government authorities;
            </li>
            <li>
              protect our, your or others’ rights, privacy, safety or property (including by making
              and defending legal claims);
            </li>
            <li>
              audit our internal processes for compliance with legal and contractual requirements or
              our internal policies;
            </li>
            <li>enforce the terms and conditions that govern the Service; and</li>
            <li>
              prevent, identify, investigate and deter fraudulent, harmful, unauthorized, unethical
              or illegal activity, including cyberattacks and identity theft.
            </li>
          </ul>
        </div>
        <p>
          <strong>With your consent.</strong> In some cases, we may specifically ask for your
          consent to collect, use, or share your personal information for further purposes, if those
          purposes are not compatible with the initial purpose for which that personal information
          was collected.
        </p>
        <p>
          <strong>Data sharing in the context of corporate transactions,</strong> we may share
          certain personal information in the context of actual or prospective corporate
          transactions – for more information, see
          <a routerLink="/privacy-policy" fragment="section-3"
            >How we share your personal information</a
          >, below.
        </p>
      </div>
    </section>

    <section class="legal-section" aria-labelledby="section-3">
      <h2 id="section-3" class="legal-section__title title-small-dark">
        3. How we share your personal information
      </h2>
      <div class="legal-section__body">
        <p>
          We may share your personal information with the following parties and as otherwise
          described in this Privacy Policy, in other applicable notices, or at the time of
          collection.
        </p>
        <p>
          <strong>Affiliates.</strong> In some cases, our corporate parent, subsidiaries, and
          affiliates might have access to personal information.
        </p>
        <p>
          <strong>Vendors.</strong> Third parties that provide services on our behalf or help us
          operate and improve the Service or our business.
        </p>
        <p>
          <strong>Payment processors.</strong> Any payment card information you use to make a
          purchase on the Service is collected and processed directly by our payment processors,
          such as Stripe. Stripe may use your payment data in accordance with its privacy policy,
          available at
          <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer"
            >https://stripe.com/privacy</a
          >.
        </p>
        <p>
          <strong>Third parties designated by you.</strong> We may share your personal information
          with third parties where you have instructed us or provided your consent to do so.
        </p>
        <p>
          <strong>Sponsor Partners.</strong> Third parties with whom we partner, including parties
          with whom we co-sponsor events or promotions, with whom we jointly offer products or
          services, or whose products or services may be of interest to you.
        </p>
        <p>
          <strong>Professional advisors.</strong> Professional advisors, such as lawyers, auditors,
          bankers and insurers, where necessary in the course of the professional services that they
          render to us.
        </p>
        <p>
          <strong>Authorities and others.</strong> Law enforcement, government authorities, and
          private parties in litigation, as we believe in good faith to be necessary or appropriate
          for the
          <a routerLink="/privacy-policy" fragment="compliance-and-protection"
            >Compliance and protection purposes</a
          >
          described above.
        </p>
        <p>
          <strong>Corporate transactions.</strong> We may disclose personal information in the
          context of actual or prospective business transactions (e.g., investments in EVRN,
          financing of EVRN, public stock offerings, the sale, transfer or merger of all or part of
          our business, assets or shares, liquidation or reorganization). For example, we may need
          to share certain personal information with prospective counterparties and their advisers.
          We may disclose your personal information to an acquirer, successor, or assignee of EVRN
          or a portion of our business assets as part of any merger, acquisition, sale of assets, or
          similar transaction, and/or in the event of an insolvency, bankruptcy, or receivership in
          which personal information is transferred to one or more third parties as one of our
          business assets.
        </p>
        <p>
          <strong>Other users and the public.</strong> Your Profile data and some user-generated
          content may be visible to other users of the Service and the public. For example, other
          users of the Service may have access to your information if you choose to make your
          profile or other personal information available to them through the Service, including in
          relation to experiences made available through the Service, when you communicate through
          the Service, provide comments, reviews, photos, videos, survey responses, or share other
          content. This information can be seen, collected and used by others, including being
          cached, copied, screen captured or stored elsewhere by others (e.g., search engines), and
          we are not responsible for any such use of this information. Once you publish content, it
          may be copied and stored on third party systems where you might not have the ability to
          have it deleted.
        </p>
      </div>
    </section>

    <section class="legal-section" aria-labelledby="section-4">
      <h2 id="section-4" class="legal-section__title title-small-dark">4. Data retention</h2>
      <div class="legal-section__body">
        <p>
          We generally retain personal information to fulfill the purposes for which we collected
          it, including for the purposes of satisfying any legal, accounting, or reporting
          requirements, to establish or defend legal claims, or for fraud prevention purposes. To
          determine the appropriate retention period for personal information, we may consider
          factors such as the length of time we have an ongoing relationship with you and provide
          services to you; the amount, nature, and sensitivity of the personal information; the
          potential risk of harm from unauthorized use or disclosure of your personal information;
          the purposes for which we process your personal information and whether we can achieve
          those purposes through other means; whether there is a legal obligation to which we are
          subject (for example, certain laws require us to keep records of your transactions for a
          certain period of time before we can delete them); whether retention is advisable in light
          of our legal position, such as in regard to applicable statutes of limitations, litigation
          or regulatory investigations; and applicable legal requirements. For example, we will
          retain your personal information for as long as you have an account with us or keep using
          our services, and the length of time thereafter during which we may have a legitimate need
          to reference your personal information to address issues that may arise. When we no longer
          require the personal information we have collected about you, we may either delete it or
          de-identify it.
        </p>
      </div>
    </section>

    <section class="legal-section" aria-labelledby="section-5">
      <h2 id="section-5" class="legal-section__title title-small-dark">5. Your choices</h2>
      <div class="legal-section__body">
        <p>In this section, we describe the choices available to you.</p>
        <p>
          <strong>Access or update your information.</strong> If you have registered for an account
          with us through the Service, you may review and update certain account information by
          logging into the account and navigating to your account profile and settings.
        </p>
        <p id="opt-out-of-communications">
          <strong>Opt-out of communications.</strong> You may opt-out of marketing-related emails by
          following the opt-out or unsubscribe instructions at the bottom of the email, or by
          <a routerLink="/privacy-policy" fragment="section-11">contacting us</a>. It may take time
          for your opt-out to be effective. Please note that if you choose to opt-out of
          marketing-related emails, you may continue to receive service-related and other
          non-marketing emails.
        </p>
        <p>
          <strong>Cookies.</strong> For information about cookies employed by the Service and how to
          control them, see our <a routerLink="/cookie-policy">Cookie Policy</a>.
        </p>
        <p>
          <strong>Declining to provide information.</strong> We need to collect personal information
          to provide certain services. If you do not provide the information we identify as required
          or mandatory, we may not be able to provide those services.
        </p>
      </div>
    </section>

    <section class="legal-section" aria-labelledby="section-6">
      <h2 id="section-6" class="legal-section__title title-small-dark">
        6. Other sites and services
      </h2>
      <div class="legal-section__body">
        <p>
          The Service may contain links to websites, mobile applications, and other online services
          operated by third parties. In addition, our content may be integrated into web pages or
          other online services that are not associated with us. These links and integrations are
          not an endorsement of, or representation that we are affiliated with, any third party. We
          do not control websites, mobile applications or online services operated by third parties,
          and we are not responsible for their actions. We encourage you to read the privacy
          policies of the other websites, mobile applications and online services you use.
        </p>
      </div>
    </section>

    <section class="legal-section" aria-labelledby="section-7">
      <h2 id="section-7" class="legal-section__title title-small-dark">7. Security</h2>
      <div class="legal-section__body">
        <p>
          We employ technical, organizational and physical safeguards that are designed to protect
          the personal information we collect. However, security risk is inherent in all Internet
          and information technologies, and we cannot guarantee the security of your personal
          information.
        </p>
      </div>
    </section>

    <section class="legal-section" aria-labelledby="section-8">
      <h2 id="section-8" class="legal-section__title title-small-dark">
        8. International data transfer
      </h2>
      <div class="legal-section__body">
        <p>
          We are headquartered in the United States and may use service providers that operate in
          other countries. Your personal information may be transferred to the United States or
          other locations where privacy laws may not be as protective as those in your state,
          province, or country.
        </p>
      </div>
    </section>

    <section class="legal-section" aria-labelledby="section-9">
      <h2 id="section-9" class="legal-section__title title-small-dark">9. Children and teens</h2>
      <div class="legal-section__body">
        <p>
          The Service is not intended for use by anyone under 18 years of age. If you are a parent
          or guardian of a minor from whom you believe we have collected personal information in a
          manner prohibited by law, or if information was provided on your behalf when you were
          under 18, please <a routerLink="/privacy-policy" fragment="section-11">contact us</a>. If
          we learn that we have collected personal information through the Service from a child
          without the consent of the child’s parent or guardian as required by law, we will comply
          with applicable legal requirements to delete the information.
        </p>
      </div>
    </section>

    <section class="legal-section" aria-labelledby="section-10">
      <h2 id="section-10" class="legal-section__title title-small-dark">
        10. Changes to this Privacy Policy
      </h2>
      <div class="legal-section__body">
        <p>
          The “LAST UPDATED” legend at the top of this Privacy Policy indicates when this Privacy
          Policy was last revised. We reserve the right to modify this Privacy Policy at any time.
          We will notify you by updating the “Last Updated” date. Depending on the kind of change we
          make, we may also notify you directly or ask for your consent to the change. Any
          modifications to this Privacy Policy will be effective upon our posting the modified
          version or as otherwise indicated at the time of posting.
        </p>
      </div>
    </section>

    <section class="legal-section" aria-labelledby="section-11">
      <h2 id="section-11" class="legal-section__title title-small-dark">11. How to contact us</h2>
      <div class="legal-section__body">
        <ul>
          <li>
            Email:
            <a href="mailto:privacy@evrn.world" target="_blank" rel="noopener noreferrer"
              >privacy&#64;evrn.world</a
            >
          </li>
          <li>
            Website:
            <a href="https://evrn.world" target="_blank" rel="noopener noreferrer">evrn.world</a>
          </li>
        </ul>
      </div>
    </section>

    <section class="legal-section" aria-labelledby="section-12">
      <h2 id="section-12" class="legal-section__title title-small-dark">
        12. U.S. State Privacy Rights Notice at Collection
      </h2>
      <div class="legal-section__body">
        <p>
          Except as otherwise provided, this section applies to residents of California and other
          states to the extent they have privacy laws applicable to us that grant their residents
          the rights described below (collectively the “State Privacy Laws”).
        </p>
        <p>
          This State Privacy Rights Notice At Collection is part of our full
          <a routerLink="/privacy-policy" fragment="section-1">Privacy Policy</a>.
        </p>
        <p>
          This section describes how we collect, retain, use, disclose, sell and share for the
          purposes of targeted advertising personal information of residents of these states and the
          rights these users may have with respect to their personal information. Please note that
          not all rights listed below may be afforded to all individuals and that if your state does
          not afford you these rights, you may not be able to exercise these rights. In addition, we
          may not be able to process your access, correction or deletion request if you do not
          provide us with sufficient detail to allow us to confirm your identity and understand and
          respond to your request.
        </p>
        <p>
          For purposes of this section, the term “personal information” means information that
          relates to an identified or identifiable natural person, or that is reasonably capable of
          being used to identify, contact, or precisely locate a natural person, household, or a
          particular computing system or device.
        </p>
        <p>
          We do not attempt to reidentify deidentified information derived from personal
          information, except for the purpose of testing whether our deidentification processes
          comply with applicable law.
        </p>
        <div>
          <p>
            <strong>Your privacy rights.</strong> You may request to exercise the rights listed
            below. We will respond to your request in accordance with applicable law. We may decline
            to honor your request where an exception applies.
          </p>
          <ul>
            <li>
              <strong>Information/To know</strong>
              <ul>
                <li>You can request to know whether we process your personal information.</li>
                <li>
                  You can request the following information about how we have collected and used
                  your personal information during the past 12 months and how we will continue to do
                  so:
                  <ul>
                    <li>The categories of personal information that we have collected.</li>
                    <li>The categories of sources from which we collected personal information.</li>
                    <li>
                      The business or commercial purpose for collecting, selling, and/or sharing
                      personal information for targeted advertising purposes.
                    </li>
                    <li>
                      The categories of personal information that we sold or shared for targeted
                      advertising purposes, and the categories of third parties to whom this
                      information was sold or shared, categorized by the type of personal
                      information for each type of third party.
                    </li>
                    <li>
                      The categories of personal information that we disclosed for business purposes
                      and the types of entities to whom this information was disclosed.
                    </li>
                    <li>
                      The specific third parties to which we have disclosed personal information.
                    </li>
                  </ul>
                </li>
              </ul>
            </li>
            <li>
              <strong>Access.</strong> You can request a portable copy or representative summary of
              the personal information that we have collected about you.
            </li>
            <li>
              <strong>Correction.</strong> You can ask us to correct inaccurate personal information
              that we have collected about you.
            </li>
            <li>
              <strong>Deletion.</strong> You can ask us to delete the personal information that we
              have collected from or about you.
            </li>
            <li>
              <strong>Revoke consent.</strong> You may have the right to revoke your consent to our
              processing of your personal information.
            </li>
            <li>
              <strong>Opt-out.</strong>
              <ul>
                <li>
                  <strong>Opt-out of targeted advertising.</strong> We do not process personal
                  information for targeted advertising purposes. You can opt out of the use of your
                  personal information for targeted advertising purposes.
                </li>
                <li>
                  <strong>Opt-out of sale of your Personal Information.</strong> We do not “sell”
                  your personal information.
                </li>
                <li>
                  <strong>Automated decision making.</strong> We do not use your personal
                  information to engage in automated processing or profiling personal information to
                  evaluate, analyze, or predict personal aspects related to your economic situation,
                  health, personal preferences, interests, reliability, behavior, location, or
                  movements to make a decision that produces legal or similarly significant effects.
                </li>
              </ul>
            </li>
            <li>
              <strong>Sensitive Personal Information</strong>
              <ul>
                <li>
                  <strong
                    >California Residents: Limit processing of Sensitive Personal
                    Information.</strong
                  >
                  We collect and maintain credentials that allow you to access an account within the
                  Services. These credentials constitute “Sensitive Personal Information” under the
                  CCPA. We use this Sensitive Personal Information as necessary for certain business
                  purposes, including providing goods or services as requested; ensuring safety,
                  security; and integrity; countering wrongful or unlawful actions; short term
                  transient use such as displaying first party, non-personalized advertising;
                  performing services for our business, including maintaining and servicing
                  accounts, providing customer service, processing or fulfilling orders and
                  transactions, verifying customer information, processing payments, providing
                  financing, providing analytic services, providing storage, or providing similar
                  services on behalf of our business; activities relating to quality and safety
                  control or product improvement; and other collection and processing that is not
                  for the purpose of inferring characteristics about an individual.
                </li>
              </ul>
            </li>
          </ul>
        </div>
        <p>
          We do not process Sensitive Personal Information for purposes beyond those listed above.
        </p>
        <ul>
          <li>
            <strong>Nondiscrimination.</strong> You are entitled to exercise the rights described
            above free from discrimination as prohibited by the State Privacy Laws.
          </li>
          <li>
            <strong>Appeal.</strong> You can appeal our denial of any request validly submitted.
          </li>
        </ul>
        <p>
          <strong>Exercising your right to know, access, correction, deletion, and appeal.</strong>
          You may submit requests at
          <a
            href="https://docs.google.com/forms/d/e/1FAIpQLSetZvfVYs57YLpWBAYrAbhI777YFy1HouAp86n3dXmij438zg/viewform"
            target="_blank"
            rel="noopener noreferrer"
            >our online request form</a
          >
          or via email to
          <a href="mailto:privacy@evrn.world" target="_blank" rel="noopener noreferrer"
            >privacy&#64;evrn.world</a
          >.
        </p>
        <p>
          We cannot process your request if you do not provide us with sufficient detail to allow us
          to understand and respond to it.
        </p>
        <p>
          <strong>Verification of Identity; Authorized agents.</strong> We may need to verify your
          identity to process your know, access, correction, deletion, or appeal requests and
          reserve the right to confirm your residency. We may need to request additional information
          in order to verify your identity and protect against fraudulent requests. If you maintain
          a password-protected account with us, we may verify your identity through our existing
          authentication practices for your account and require you to reauthenticate yourself
          before disclosing or deleting your personal information. If you make a request to delete,
          we may ask you to confirm your request before we delete your personal information.
        </p>
        <p>
          Under some State Privacy Laws, you may enable an authorized agent to make a request on
          your behalf. However, we may need to verify your authorized agent’s identity and authority
          to act on your behalf. Depending on the kind of request you have made, we may require a
          copy of a valid power of attorney given to your authorized agent pursuant to applicable
          law. If you have not provided your agent with such a power of attorney, we may ask you to
          take additional steps permitted by law to verify that your request is authorized, such as
          by providing your agent with written and signed permission to exercise your State Privacy
          Law rights on your behalf, the information we request to verify your identity, or
          confirmation that you have given the authorized agent permission to submit the request.
        </p>
        <p>
          <strong>Personal information that we collect, use and disclose.</strong> We have
          summarized the personal information we collect and may disclose, sell to or share with
          third parties by reference below to both the categories of personal information defined in
          the “Personal Information we collect,” “How we use your personal information,” and “How we
          share your personal information” sections of this Policy above and the categories of
          personal information specified in state law. This table describes our practices currently
          and during the 12 months preceding the effective date of this Privacy Policy. Information
          you voluntarily provide to us, such as in free-form webforms, may contain other categories
          of personal information not described below.
        </p>
        <div class="legal-table__wrapper">
          <table class="legal-table">
            <thead>
              <tr>
                <th scope="col" title="Personal Information (“PI”) we collect">
                  <span>Personal Information (“PI”) we collect</span>
                </th>
                <th scope="col" title="California statutory category of PI">
                  <span>California statutory category of PI</span>
                </th>
                <th scope="col" title="Business/commercial purpose for PI collection">
                  <span>Business/commercial purpose for PI collection</span>
                </th>
                <th
                  scope="col"
                  title="Categories of service providers or contractors to whom we “disclose” PI for a business purpose"
                >
                  <span>
                    Categories of service providers or contractors to whom we “disclose” PI for a
                    business purpose
                  </span>
                </th>
                <th
                  scope="col"
                  title="Categories of third parties with whom we “sell” or “share” PI for cross-context behavioral advertising purposes"
                >
                  <span>
                    Categories of third parties with whom we “sell” or “share” PI for cross-context
                    behavioral advertising purposes
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Contact data</td>
                <td>
                  <ul class="legal-cell-list">
                    <li>Identifiers</li>
                    <li>Customer records</li>
                  </ul>
                </td>
                <td>
                  <ul class="legal-cell-list">
                    <li>Service delivery and operations</li>
                    <li>Direct marketing</li>
                    <li>Compliance and protection</li>
                    <li>Analytics</li>
                    <li>Corporate transactions</li>
                  </ul>
                </td>
                <td>
                  <ul class="legal-cell-list">
                    <li>Vendors</li>
                    <li>Payment processors</li>
                    <li>Third parties designated by you</li>
                  </ul>
                </td>
                <td>N/A</td>
              </tr>
              <tr>
                <td>Demographic data</td>
                <td>Customer records</td>
                <td>
                  <ul class="legal-cell-list">
                    <li>Service delivery and operations</li>
                    <li>Research and development</li>
                    <li>Direct marketing</li>
                    <li>Compliance and protection</li>
                    <li>Analytics</li>
                    <li>Corporate transactions</li>
                  </ul>
                </td>
                <td></td>
                <td>N/A</td>
              </tr>
              <tr>
                <td>Profile data</td>
                <td>
                  <ul class="legal-cell-list">
                    <li>Identifiers</li>
                    <li>Customer records</li>
                    <li>Employment information</li>
                    <li>Internet activity</li>
                  </ul>
                </td>
                <td>
                  <ul class="legal-cell-list">
                    <li>Service delivery and operations</li>
                    <li>Research and development</li>
                    <li>Direct marketing</li>
                    <li>Compliance and protection</li>
                    <li>Analytics</li>
                    <li>Corporate transactions</li>
                  </ul>
                </td>
                <td></td>
                <td>N/A</td>
              </tr>
              <tr>
                <td>Payment data</td>
                <td>Customer records</td>
                <td>
                  <ul class="legal-cell-list">
                    <li>Service delivery and operations</li>
                    <li>Compliance and protection</li>
                    <li>Corporate transactions</li>
                  </ul>
                </td>
                <td></td>
                <td></td>
              </tr>
              <tr>
                <td>Communication data</td>
                <td>
                  <ul class="legal-cell-list">
                    <li>Commercial information</li>
                    <li>Internet activity</li>
                  </ul>
                </td>
                <td>
                  <ul class="legal-cell-list">
                    <li>Service delivery and operations</li>
                    <li>Direct marketing</li>
                    <li>Compliance and protection</li>
                    <li>Analytics</li>
                    <li>Corporate transactions</li>
                  </ul>
                </td>
                <td></td>
                <td>N/A</td>
              </tr>
              <tr>
                <td>Marketing Data</td>
                <td>
                  <ul class="legal-cell-list">
                    <li>Commercial information</li>
                    <li>Internet activity</li>
                    <li>Inferences</li>
                  </ul>
                </td>
                <td>
                  <ul class="legal-cell-list">
                    <li>Service delivery and operations</li>
                    <li>Direct marketing</li>
                    <li>Compliance and protection</li>
                    <li>Analytics</li>
                    <li>Corporate transactions</li>
                  </ul>
                </td>
                <td></td>
                <td>N/A</td>
              </tr>
              <tr>
                <td>User Content</td>
                <td>
                  <ul class="legal-cell-list">
                    <li>Audio/video/visual data</li>
                    <li>Internet activity</li>
                  </ul>
                </td>
                <td>
                  <ul class="legal-cell-list">
                    <li>Service delivery and operations</li>
                    <li>Research and development</li>
                    <li>Compliance and protection</li>
                    <li>Analytics</li>
                    <li>Corporate transactions</li>
                  </ul>
                </td>
                <td></td>
                <td>N/A</td>
              </tr>
              <tr>
                <td>Device data</td>
                <td>
                  <ul class="legal-cell-list">
                    <li>Identifiers</li>
                    <li>Internet activity</li>
                  </ul>
                </td>
                <td>
                  <ul class="legal-cell-list">
                    <li>Service delivery and operations</li>
                    <li>Research and development</li>
                    <li>Direct marketing</li>
                    <li>Compliance and protection</li>
                    <li>Analytics</li>
                    <li>Corporate transactions</li>
                  </ul>
                </td>
                <td></td>
                <td>N/A</td>
              </tr>
              <tr>
                <td>Communication interaction data</td>
                <td>
                  <ul class="legal-cell-list">
                    <li>Commercial information</li>
                    <li>Internet activity</li>
                  </ul>
                </td>
                <td>
                  <ul class="legal-cell-list">
                    <li>Service delivery and operations</li>
                    <li>Research and development</li>
                    <li>Direct marketing</li>
                    <li>Compliance and protection</li>
                    <li>Analytics</li>
                    <li>Corporate transactions</li>
                  </ul>
                </td>
                <td></td>
                <td>N/A</td>
              </tr>
              <tr>
                <td>Online activity data</td>
                <td>
                  <ul class="legal-cell-list">
                    <li>Commercial information</li>
                    <li>Internet activity</li>
                  </ul>
                </td>
                <td>
                  <ul class="legal-cell-list">
                    <li>Service delivery and operations</li>
                    <li>Research and development</li>
                    <li>Direct marketing</li>
                    <li>Compliance and protection</li>
                    <li>Analytics</li>
                    <li>Corporate transactions</li>
                  </ul>
                </td>
                <td></td>
                <td>N/A</td>
              </tr>
              <tr>
                <td>Tracking technology data</td>
                <td>
                  <ul class="legal-cell-list">
                    <li>Identifiers</li>
                    <li>Internet activity</li>
                  </ul>
                </td>
                <td>
                  <ul class="legal-cell-list">
                    <li>Service delivery and operations</li>
                    <li>Research and development</li>
                    <li>Direct marketing</li>
                    <li>Compliance and protection</li>
                    <li>Analytics</li>
                    <li>Corporate transactions</li>
                  </ul>
                </td>
                <td></td>
                <td>N/A</td>
              </tr>
              <tr>
                <td>Biometric data</td>
                <td>Biometric data</td>
                <td>
                  <ul class="legal-cell-list">
                    <li>Service delivery and operations</li>
                    <li>Research and development</li>
                  </ul>
                </td>
                <td></td>
                <td>N/A</td>
              </tr>
              <tr>
                <td>Inferences from the above kinds of personal information</td>
                <td>Inferences</td>
                <td>
                  <ul class="legal-cell-list">
                    <li>Service delivery and operations</li>
                    <li>Research and development</li>
                    <li>Direct marketing</li>
                    <li>Compliance and protection</li>
                    <li>Analytics</li>
                    <li>Corporate transactions</li>
                  </ul>
                </td>
                <td></td>
                <td>N/A</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          See our full
          <a routerLink="/privacy-policy" fragment="section-1">Privacy Policy</a> for descriptions
          of each entry in this table.
        </p>
        <p>
          <strong>Consumers under 18.</strong> We do not have actual knowledge that we have
          collected, sold, or shared the personal information of residents who are under 18 years of
          age.
        </p>
        <p>
          <strong>Additional information for Nevada residents.</strong> Nevada residents have the
          right to opt-out of the sale, as defined, of certain personal information for monetary
          consideration. While we do not currently engage in such sales, if you are a Nevada
          resident and would like to make a request to opt out of any potential future sales, please
          email
          <a href="mailto:privacy@evrn.world" target="_blank" rel="noopener noreferrer"
            >privacy&#64;evrn.world</a
          >.
        </p>
        <p>
          <strong>Retention Period for Personal Information.</strong> We generally retain personal
          information to fulfill the purposes for which we collected it, including for the purposes
          of satisfying any legal, accounting, or reporting requirements, to establish or defend
          legal claims, or for fraud prevention purposes. To determine the appropriate retention
          period for personal information, we may consider factors such as the length of time we
          have an ongoing relationship with you and provide services to you; the amount, nature, and
          sensitivity of the personal information; the potential risk of harm from unauthorized use
          or disclosure of your personal information; the purposes for which we process your
          personal information and whether we can achieve those purposes through other means;
          whether there is a legal obligation to which we are subject (for example, certain laws
          require us to keep records of your transactions for a certain period of time before we can
          delete them); whether retention is advisable in light of our legal position, such as in
          regard to applicable statutes of limitations, litigation or regulatory investigations; and
          applicable legal requirements. For example, we will retain your personal information for
          as long as you have an account with us or keep using our services, and the length of time
          thereafter during which we may have a legitimate need to reference your personal
          information to address issues that may arise. When we no longer require the personal
          information we have collected about you, we may either delete it or de-identify it.
        </p>
        <p>
          <strong>Additional information for Connecticut residents.</strong> We do not collect, use,
          or sell personal data for the purpose of training large language models (“LLMs”).
        </p>
        <p>
          <strong>Contact Us.</strong> If you have questions or concerns about our privacy policies
          or information practices, please contact us using the contact details set forth in the
          <a routerLink="/privacy-policy" fragment="section-11">How to contact us</a> section,
          above.
        </p>
      </div>
    </section>

    <section class="legal-section" aria-labelledby="section-13">
      <h2 id="section-13" class="legal-section__title title-small-dark">
        13. Notice to European users
      </h2>
      <div class="legal-section__body">
        <h3 class="legal-section__subtitle">General</h3>
        <p>
          <strong>Where this Notice to European users applies.</strong> The information provided in
          this “Notice to European users” section applies only to individuals in the United Kingdom
          and the European Economic Area (i.e., “Europe” as defined at the top of this Privacy
          Policy).
        </p>
        <p>
          <strong>Personal information.</strong> References to “personal information” in this
          Privacy Policy should be understood to include a reference to “personal data” (as defined
          in the GDPR) – i.e., information about individuals from they are either directly
          identified or can be identified.
        </p>
        <p>
          <strong>Controller.</strong> Cuebric, Inc. d/b/a EVRN is the controller in respect of the
          processing of your personal information covered by this Privacy Policy for purposes of
          European data protection legislation (i.e., the
          <a
            href="https://eur-lex.europa.eu/legal-content/EN/TXT/HTML/?uri=CELEX:32016R0679&from=EN"
            target="_blank"
            rel="noopener noreferrer"
            >EU GDPR</a
          >
          and the so-called ‘<a
            href="https://assets.publishing.service.gov.uk/government/uploads/system/uploads/attachment_data/file/969514/20201102_-_GDPR_-__MASTER__Keeling_Schedule__with_changes_highlighted__V4.pdf"
            target="_blank"
            rel="noopener noreferrer"
            >UK GDPR’</a
          >
          (as and where applicable, the “GDPR”)). See the ‘How to contact us’ section above for our
          contact details.
        </p>
        <p>
          <strong>Our GDPR Representatives.</strong> We have appointed the following representatives
          in Europe as required by the GDPR – you can also contact them directly should you wish:
        </p>
        <div class="legal-table__wrapper">
          <table class="legal-table">
            <tbody>
              <tr>
                <td>
                  <p><strong>Our Representative in the EU.</strong></p>
                  <p>
                    European Data Protection Office (EDPO). You can contact EDPO regarding matters
                    pertaining to the GDPR:
                  </p>
                  <ul class="legal-cell-list">
                    <li>
                      by using EDPO’s online request form:
                      <a
                        href="https://edpo.com/gdpr-data-request/"
                        target="_blank"
                        rel="noopener noreferrer"
                        >https://edpo.com/gdpr-data-request/</a
                      >
                    </li>
                    <li>by writing to EDPO at Avenue Huart Hamoir 71, 1030 Brussels, Belgium</li>
                  </ul>
                </td>
                <td>
                  <p><strong>Our Representative in the UK.</strong></p>
                  <p>
                    EDPO UK Ltd. You can contact EDPO UK regarding matters pertaining to the UK
                    GDPR:
                  </p>
                  <ul class="legal-cell-list">
                    <li>
                      by using EDPO’s online request form:
                      <a
                        href="https://edpo.com/uk-gdpr-data-request/"
                        target="_blank"
                        rel="noopener noreferrer"
                        >https://edpo.com/uk-gdpr-data-request/</a
                      >
                    </li>
                    <li>
                      by writing to EDPO UK at Unit 33, Waterside, Schooner Court, 44-48 Wharf Road,
                      London, N1 7UX, United Kingdom
                    </li>
                  </ul>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 class="legal-section__subtitle">Our legal bases for processing</h3>
        <p>
          In respect of each of the purposes for which we use your personal information, the GDPR
          requires us to ensure that we have a “legal basis” for that use.
        </p>
        <div>
          <p>
            Our legal bases for processing your personal information described in this Privacy
            Policy are listed below.
          </p>
          <ul>
            <li>
              Where we need to perform a contract, we are about to enter into or have entered into
              with you (“Contractual Necessity”).
            </li>
            <li>
              Where it is necessary for our legitimate interests and your interests and fundamental
              rights do not override those interests (“Legitimate Interests”). More detail about the
              specific legitimate interests pursued in respect of each Purpose we use your personal
              information for is set out in the table below.
            </li>
            <li>
              Where we need to comply with a legal or regulatory obligation (“Compliance with Law”).
            </li>
            <li>
              Where we have your specific consent to carry out the processing for the Purpose in
              question (“Consent”).
            </li>
          </ul>
        </div>
        <p>
          We have set out below, in a table format, the legal bases we rely on in respect of the
          relevant Purposes for which we use your personal information – for more information on
          these Purposes and the data types involved, see ‘How we use your personal information’.
        </p>
        <div class="legal-table__wrapper">
          <table class="legal-table">
            <thead>
              <tr>
                <th scope="col" title="Purpose"><span>Purpose</span></th>
                <th scope="col" title="Categories of personal information involved">
                  <span>Categories of personal information involved</span>
                </th>
                <th scope="col" title="Legal basis"><span>Legal basis</span></th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Service delivery and operations</td>
                <td>
                  <ul class="legal-cell-list">
                    <li>Contact data</li>
                    <li>Demographic data</li>
                    <li>Profile Data</li>
                    <li>Communications data</li>
                    <li>User content data</li>
                    <li>Payment data</li>
                    <li>Data from Third Party Services</li>
                    <li>Device data</li>
                    <li>Location data</li>
                    <li>Communication interaction data</li>
                    <li>Online activity data</li>
                    <li>Biometric data</li>
                  </ul>
                </td>
                <td>
                  <p><strong>Delivery and operations</strong></p>
                  <p>Contractual Necessity.</p>
                  <p>
                    Legitimate Interests. We have a legitimate interest in ensuring the ongoing
                    security and proper operation of our Service, our business and associated IT
                    services, systems and networks.
                  </p>
                  <p>Consent, where we.</p>
                  <p><strong>Service personalization</strong></p>
                  <p>
                    Legitimate Interests. We have a legitimate interest in providing you with a good
                    service via the Service, which is personalized to you and that remembers your
                    selections and preferences.
                  </p>
                  <p>
                    Consent, in circumstances or in jurisdictions where consent is required under
                    applicable data protection laws in respect of any optional processing relevant
                    to personalization.
                  </p>
                </td>
              </tr>
              <tr>
                <td>Service improvement, research and analytics</td>
                <td>
                  <ul class="legal-cell-list">
                    <li>Contact data</li>
                    <li>Demographic data</li>
                    <li>Profile data</li>
                    <li>User content data</li>
                    <li>Device data</li>
                    <li>Location data</li>
                    <li>Communication interaction data</li>
                    <li>Online activity data</li>
                    <li>Biometric data</li>
                  </ul>
                </td>
                <td>
                  <p>
                    Legitimate Interests. We have a legitimate interest in providing you with a good
                    service, which is personalised to you and that remembers your selections and
                    preferences. We also have a legitimate interest in taking steps to preserve our
                    users’ privacy as we research how they use our Service.
                  </p>
                  <p>
                    Consent, in respect of any optional cookies used for this purpose or where we.
                  </p>
                </td>
              </tr>
              <tr>
                <td>Direct marketing</td>
                <td>
                  <ul class="legal-cell-list">
                    <li>Contact data</li>
                    <li>Demographic data</li>
                    <li>Profile data</li>
                    <li>Communications data</li>
                    <li>Marketing data</li>
                    <li>Communication interaction data</li>
                  </ul>
                </td>
                <td>
                  <p>
                    Legitimate Interests. We have a legitimate interest in promoting our operations
                    and goals as an organisation and sending marketing communications for that
                    purpose.
                  </p>
                  <p>
                    Consent, in circumstances or in jurisdictions where consent is required under
                    applicable data protection laws to the sending of any given marketing
                    communications.
                  </p>
                </td>
              </tr>
              <tr>
                <td>Compliance and protection</td>
                <td>Any and all data types relevant in the circumstances</td>
                <td>
                  <p>Compliance with Law.</p>
                  <p>
                    Legitimate interest. Where Compliance with Law is not applicable, we and any
                    relevant third parties have a legitimate interest in participating in,
                    supporting, and following legal process and requests, including through
                    co-operation with authorities. We and any relevant third parties may also have a
                    legitimate interest of ensuring the protection, maintenance, and enforcement of
                    our and their rights, property, and/or safety.
                  </p>
                  <p>Consent, where we.</p>
                </td>
              </tr>
              <tr>
                <td>Data sharing in the context of corporate transactions</td>
                <td>Any and all data types relevant in the circumstances</td>
                <td>
                  Legitimate interest. We have a legitimate interest in providing information to
                  relevant third parties who are involved in an actual or prospective corporate
                  event (including to enable them to investigate—and, where relevant, to continue to
                  operate—all or relevant part(s) of our operations).
                </td>
              </tr>
              <tr>
                <td>Further uses</td>
                <td>Any and all data types relevant in the circumstances</td>
                <td>
                  <p>
                    The original legal basis relied upon, if the relevant further use is compatible
                    with the initial purpose for which the Personal Information was collected.
                  </p>
                  <p>
                    Consent, if the relevant further use is not compatible with the initial purpose
                    for which the personal information was collected.
                  </p>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <p>
          <strong>No sensitive personal information.</strong> We ask that you not provide us with
          any protected or sensitive personal details (e.g., information related to racial or ethnic
          origin, political opinions, religion or other beliefs, health, genetic characteristics,
          information related to sex life or sexual orientation, trade union membership, or
          information pertaining to criminal convictions) on or through the services, or otherwise
          to us. If you provide us with any sensitive personal information to us when you use the
          services, you must consent to our processing and use of such sensitive personal
          information in accordance with this Privacy Policy. If you do not consent to our
          processing and use of such sensitive personal information, you must not submit such
          sensitive personal information through our services.
        </p>
        <p>
          <strong>No Automated Decision-Making and Profiling.</strong> As part of the Service, we do
          not engage in automated decision-making and/or profiling, which produces legal or
          similarly significant effects.
        </p>

        <h3 class="legal-section__subtitle">Your rights</h3>
        <div>
          <p>
            <strong>General.</strong> European data protection laws give you certain rights
            regarding your personal information. If you are located in Europe, you may ask us to
            take the following actions in relation to your personal information that we hold:
          </p>
          <ul>
            <li>
              <strong>Access.</strong> Provide you with information about our processing of your
              personal information and give you access to your personal information.
            </li>
            <li>
              <strong>Correct.</strong> Update or correct inaccuracies in your personal information.
            </li>
            <li>
              <strong>Delete.</strong> Delete your personal information where there is no good
              reason for us continuing to process it - you also have the right to ask us to delete
              or remove your personal information where you have exercised your right to object to
              processing (see below).
            </li>
            <li>
              <strong>Transfer.</strong> Transfer a machine-readable copy of your personal
              information to you or a third party of your choice.
            </li>
            <li>
              <strong>Restrict.</strong> Restrict the processing of your personal information, for
              example if you want us to establish its accuracy or the reason for processing it.
            </li>
            <li>
              <strong>Object.</strong> Object to our processing of your personal information where
              we are relying on Legitimate Interests – you also have the right to object where we
              are processing your personal information for direct marketing purposes.
            </li>
            <li>
              <strong>Withdraw Consent.</strong> When we use your personal information based on your
              consent, you have the right to withdraw that consent at any time.
            </li>
          </ul>
        </div>
        <p>
          <strong>Exercising These Rights.</strong> You may submit these requests by email to
          <a href="mailto:privacy@evrn.world" target="_blank" rel="noopener noreferrer"
            >privacy&#64;evrn.world</a
          >
          or our postal address provided above. We may request specific information from you to help
          us confirm your identity and process your request. Whether or not we are required to
          fulfill any request you make will depend on a number of factors (e.g., why and how we are
          processing your personal information), if we reject any request you may make (whether in
          whole or in part) we will let you know our grounds for doing so at the time, subject to
          any legal restrictions.
        </p>
        <p>
          <strong>Your Right to Complain.</strong> Depending on where you are located, you may also
          have a right to complain to us if you are not satisfied with our handling of your personal
          information. You can submit a complaint to us by email to
          <a href="mailto:privacy@evrn.world" target="_blank" rel="noopener noreferrer"
            >privacy&#64;evrn.world</a
          >. We will reply to your request as soon as we can.
        </p>
        <div>
          <p>
            <strong>Your Right to Lodge a Complaint with your Supervisory Authority.</strong> In
            addition to your rights outlined above, if you are not satisfied with our response to a
            request you make, or how we process your personal information, you can make a complaint
            to the data protection regulator in your habitual place of residence.
          </p>
          <ul>
            <li>
              For users in the European Economic Area – the contact information for the data
              protection regulator in your place of residence can be found here:
              <a
                href="https://edpb.europa.eu/about-edpb/board/members_en"
                target="_blank"
                rel="noopener noreferrer"
                >https://edpb.europa.eu/about-edpb/board/members_en</a
              >
            </li>
            <li>
              For users in the UK – the contact information for the UK data protection regulator is
              below:
              <ul>
                <li>The Information Commissioner’s Office</li>
                <li>Water Lane, Wycliffe House</li>
                <li>Wilmslow - Cheshire SK9 5AF</li>
                <li>Tel. +44 303 123 1113</li>
                <li>
                  Website:
                  <a
                    href="https://ico.org.uk/make-a-complaint/"
                    target="_blank"
                    rel="noopener noreferrer"
                    >https://ico.org.uk/make-a-complaint/</a
                  >
                </li>
              </ul>
            </li>
          </ul>
        </div>

        <h3 class="legal-section__subtitle">Data Processing outside Europe</h3>
        <p>
          We are a U.S.-based company and many of our service providers, advisers, partners or other
          recipients of data are also based in the U.S. This means that, if you use the Service,
          your personal information will necessarily be accessed and processed in the U.S. It may
          also be provided to recipients in other countries outside Europe.
        </p>
        <p>
          It is important to note that the US is not the subject of an ‘adequacy decision’ under the
          GDPR – basically, this means that the U.S. legal regime is not considered by relevant
          European bodies to provide an adequate level of protection for personal information, which
          is equivalent to that provided by relevant European laws.
        </p>
        <div>
          <p>
            Where we share your personal information with third parties who are based outside
            Europe, we try to ensure a similar degree of protection is afforded to it by making sure
            one of the following mechanisms is implemented:
          </p>
          <ul>
            <li>
              <strong>Transfers to territories with an adequacy decision.</strong> We may transfer
              your personal information to countries or territories whose laws have been deemed to
              provide an adequate level of protection for personal information by the European
              Commission or UK Government (as and where applicable) (from time to time).
            </li>
            <li>
              <strong>Transfers to territories without an adequacy decision.</strong>
              <ul>
                <li>
                  We may transfer your personal information to countries or territories whose laws
                  have not been deemed to provide such an adequate level of protection (e.g., the
                  U.S., see above).
                </li>
                <li>
                  However, in these cases:
                  <ul>
                    <li>
                      we may use specific appropriate safeguards, which are designed to give
                      personal information effectively the same protection it has in Europe – for
                      example, standard-form contracts approved by relevant authorise for this
                      purpose; or
                    </li>
                    <li>
                      in limited circumstances, we may rely on an exception, or ‘derogation’, which
                      permits us to transfer your personal information to such country despite the
                      absence of an ‘adequacy decision’ or ‘appropriate safeguards’ – for example,
                      reliance on your explicit consent to that transfer.
                    </li>
                  </ul>
                </li>
              </ul>
            </li>
          </ul>
        </div>
        <p>
          You may contact us if you want further information on the specific mechanism used by us
          when transferring your personal information out of Europe. You may have the right to
          receive a copy of the appropriate safeguards under which your personal information is
          transferred by contacting us at
          <a href="mailto:privacy@evrn.world" target="_blank" rel="noopener noreferrer"
            >privacy&#64;evrn.world</a
          >.
        </p>
      </div>
    </section>
  </div>
</section>
